import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Building2, ExternalLink } from "lucide-react";
import { STRATEGIC_ENGINEERING_PARTNER } from "../../config/partners";
import { getSaiWebsiteUrl, hasSaiWebsiteUrl } from "../../config/env";
import { QUOTATION_WORKFLOWS } from "../../config/workflow";

/**
 * EPC section — entirely separate from service cards and DEBSUN quotation workflow.
 * Large EPC / turnkey projects route to SAI Enterprises only.
 */
export default function EpcPartnerSection() {
  const url = getSaiWebsiteUrl();
  const partner = STRATEGIC_ENGINEERING_PARTNER;

  return (
    <section
      id="epc"
      className="relative py-20 sm:py-28 lg:py-36 bg-secondary/40 border-y border-border scroll-mt-[88px]"
      data-testid="epc-partner-section"
    >
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-6">
            <div className="sec-index mb-4">02C — EPC Projects</div>
            <h2 className="display display-section text-4xl md:text-6xl">
              Large Scale EPC &amp;{" "}
              <span style={{ color: "var(--gold)" }}>Turnkey</span> Engineering
              Projects.
            </h2>
            <p className="mt-6 text-base md:text-lg text-steel-text leading-relaxed max-w-xl">
              Projects of this scale are executed through our strategic engineering
              partner. DEBSUN Technologies does not execute large EPC programmes
              directly through the website quotation workflow.
            </p>

            <div className="mt-10 border border-border bg-background p-6 lg:p-8">
              <div className="font-mono-2 text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-3">
                Strategic Engineering Partner
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 border border-border flex items-center justify-center shrink-0">
                  <Building2
                    size={22}
                    strokeWidth={1.3}
                    className="text-[color:var(--gold)]"
                  />
                </div>
                <div>
                  <h3 className="display display-section text-3xl">
                    {partner.name}
                  </h3>
                  <p className="mt-3 text-sm text-steel-text leading-relaxed">
                    {partner.description}
                  </p>
                  <ul className="mt-5 space-y-2">
                    {partner.scope.map((item) => (
                      <li
                        key={item}
                        className="flex items-center gap-3 text-sm text-steel-text"
                      >
                        <span className="w-1.5 h-1.5 bg-[color:var(--gold)]" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="mt-8">
                {hasSaiWebsiteUrl() ? (
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-gold clip-frame-sm px-6 py-4 text-sm uppercase tracking-widest font-mono-2 inline-flex items-center gap-2"
                    data-testid="visit-sai-enterprises-btn"
                  >
                    Visit SAI Enterprises <ExternalLink size={14} />
                  </a>
                ) : (
                  <div
                    className="border border-border px-6 py-4 space-y-2"
                    data-testid="sai-contact-details"
                  >
                    <div className="font-mono-2 text-[10px] tracking-[0.25em] uppercase text-muted-foreground">
                      Contact SAI Enterprises
                    </div>
                    <a
                      href={`tel:+91${partner.phone}`}
                      className="block text-sm text-foreground aurora-underline"
                      data-testid="sai-contact-phone"
                    >
                      +91 {partner.phone}
                    </a>
                    <a
                      href={`mailto:${partner.email}`}
                      className="block text-sm text-foreground aurora-underline"
                      data-testid="sai-contact-email"
                    >
                      {partner.email}
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 lg:col-start-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="border border-border p-7 lg:p-9 bg-background"
              data-testid="epc-workflow-card"
            >
              <div className="font-mono-2 text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-4">
                Quotation Pathways — Kept Separate
              </div>

              <div className="mb-8">
                <div className="font-mono-2 text-[10px] tracking-[0.2em] uppercase text-[color:var(--teal)] mb-3">
                  Engineering Services
                </div>
                <ol className="space-y-2">
                  {QUOTATION_WORKFLOWS.engineeringServices.path.map((step, i) => (
                    <li key={step} className="flex items-center gap-3 text-sm">
                      <span className="font-mono-2 text-[10px] text-muted-foreground w-5">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="text-steel-text">{step}</span>
                      {i < QUOTATION_WORKFLOWS.engineeringServices.path.length - 1 && (
                        <ArrowUpRight
                          size={12}
                          className="text-muted-foreground rotate-90 ml-auto"
                        />
                      )}
                    </li>
                  ))}
                </ol>
              </div>

              <div className="hairline mb-8" />

              <div>
                <div className="font-mono-2 text-[10px] tracking-[0.2em] uppercase text-[color:var(--gold)] mb-3">
                  EPC Projects
                </div>
                <ol className="space-y-2">
                  {QUOTATION_WORKFLOWS.epcProjects.path.map((step, i) => (
                    <li key={step} className="flex items-center gap-3 text-sm">
                      <span className="font-mono-2 text-[10px] text-muted-foreground w-5">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="text-steel-text">{step}</span>
                      {i < QUOTATION_WORKFLOWS.epcProjects.path.length - 1 && (
                        <ArrowUpRight
                          size={12}
                          className="text-muted-foreground rotate-90 ml-auto"
                        />
                      )}
                    </li>
                  ))}
                </ol>
              </div>

              <p className="mt-8 text-xs text-muted-foreground leading-relaxed font-mono-2">
                These workflows are never merged. Website quotes apply to DEBSUN
                engineering services and products only.
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
